import React from 'react';

import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useAPI } from './api/useAPI';
import { useAuthenticatedQueries } from './api/useAuthenticatedQuery';

export type PlanNodes = {
  starterNodes: string[] | undefined;
  businessNodes: string[] | undefined;
};

export function usePlanIncludedNodes() {
  const { data: organization } = useCurrentOrganization();

  const { client } = useAPI();
  const planIds = [
    import.meta.env.VITE_STARTER_PLAN_ID,
    import.meta.env.VITE_BUSINESS_PLAN_ID,
  ];

  const [
    {
      data: starterNodes,
      isInitialLoading: isLoadingStarterNodes,
      isSuccess: isStarterNodesSuccess,
      error: starterNodesError,
    },
    {
      data: businessNodes,
      isInitialLoading: isLoadingBusinessNodes,
      isSuccess: isBusinessNodesSuccess,
      error: businessNodesError,
    },
  ] = useAuthenticatedQueries(
    planIds.map((planId) => ({
      queryKey: ['included-nodes', planId],
      staleTime: Infinity,
      async queryFn() {
        if (!organization?.id) {
          throw new Error('No organization.');
        }

        const { data } = await client.get<string[]>(
          `/internal/orgs/${organization.id}/plans/${planId}/included_nodes`
        );
        return data;
      },
      enabled: Boolean(organization?.id),
    }))
  );

  return React.useMemo(
    () =>
      [
        { starterNodes, businessNodes } as PlanNodes,
        {
          isLoading: isLoadingStarterNodes || isLoadingBusinessNodes,
          isSuccess: isStarterNodesSuccess || isBusinessNodesSuccess,
          error: starterNodesError || businessNodesError,
        },
      ] as const,
    [
      starterNodes,
      isLoadingStarterNodes,
      isStarterNodesSuccess,
      starterNodesError,
      businessNodes,
      isLoadingBusinessNodes,
      isBusinessNodesSuccess,
      businessNodesError,
    ]
  );
}
